import { useEffect, useState, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, Grid, Link, Container } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import Header from "../components/layout/Header";
import Calendar from "../components/ui/Calendar";
import BookTour from "../components/sections/BookTour";
import EventSchema from '../components/ui/EventSchema';
import Footer from "../components/layout/Footer";
import RSVPButton from "../components/forms/RSVPButton";
import AttendeeDisplay from "../components/sections/AttendeeDisplay";
import MetaTags from "../components/layout/MetaTags";
import EventGallery from "../components/sections/EventGallery";

import Principles from "../components/sections/Principles";
import CityNotificationForm from "../components/forms/CityNotificationForm";
import ReactGA from 'react-ga4';
import { getTour } from '../utils/booktourlist';

import { useStyles } from '../styles/styles';

const NotFound = lazy(() => import('./NotFound'));

function TourLanding() {
  const { tourId } = useParams();
  const { classes } = useStyles();
  const [attendeeRefreshTrigger, setAttendeeRefreshTrigger] = useState(0);

  // Get tour data
  const tour = tourId ? getTour(tourId) : null;

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, [tourId]);

  // If tourId is provided but tour is not found, show NotFound
  if (tourId && !tour) {
    return <NotFound />;
  }

  // Helper function to check if event is in the past
  const isEventInPast = (eventDate) => {
    if (!eventDate) return false;
    const now = new Date();
    const event = new Date(eventDate);
    return event < now;
  };

  // Determine which view to render
  const isOverviewPage = !tourId;
  const isUnconfirmedCity = tour && !tour.confirmed;
  const isConfirmedEvent = tour && tour.confirmed;
  const isPastEvent = tour && tour.date && isEventInPast(tour.date);

  // Handle RSVP success - refresh attendee data only for the current tour
  const handleRSVPSuccess = (rsvpEventId) => {
    // Only refresh if the RSVP was for the current tour being displayed
    if (tour && rsvpEventId === tour.id) {
      setAttendeeRefreshTrigger(prev => prev + 1);
    }
  };

  // Analytics tracking
  if (tour) {
    ReactGA.send({ hitType: "pageview", page: window.location.path, title: `${tour.venue} - ${tour.city} | Book Tour` });
  } else {
    ReactGA.send({ traffic_type: window.location.hostname, hitType: "pageview", page: window.location.path, title: 'Book Tour Overview' });
  }

  // Generate metadata based on the view type
  if (tour) {
    tour.path = 'book-tour';
    tour.title = `${tour.venue} - ${tour.city} | Events & Appearances`;
    tour.keywords = `Sri Kaza, ${tour.city}, Unconvention, Small Business, ${tour.venue}`;
    tour.description = isPastEvent
      ? (tour.recap ? tour.recap.split('.')[0] + '.' : tour.venueDescription)
      : `${tour.description} at ${tour.venue} in ${tour.city}. ${tour.venueDescription}`;  
  }

  const mainpage  = {
    id: 'book-tour-overview',
    title: 'Events & Appearances | Sri Kaza',
    description: 'Where Unconvention has been featured — book signings, author events, and conferences celebrating small business strategy and entrepreneurship.',
    keywords: 'Sri Kaza, Unconvention, Events, Book Signings, Conferences, Small Business Strategy',
  }

  // Format date for display
  const formatEventDate = (date, timezone) => {
    if (!date) return '';
    const eventDate = new Date(date);
    return eventDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone || 'America/Chicago'
    });
  };

  const formatEventTime = (date, timezone) => {
    if (!date) return '';
    const eventDate = new Date(date);
    return eventDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone || 'America/Chicago',
      timeZoneName: 'short'
    });
  };

  // Render function for confirmed events (both past and upcoming)
  const renderConfirmedEvent = () => (
    <>
      <EventSchema tour={tour} />
      <Box key={tour.id} sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        height: { xs: 'auto', sm: 324 },
        backgroundImage: 'url(' + tour.image + ')',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        width: '100%'
      }}>
        <Container maxWidth="lg" sx={{ pt: '20px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flexDirection: 'column', display: 'grid' }}>
            <Calendar date={tour.date} />
            <Box
              sx={{
                alignSelf: 'flex-end',
                flexDirection: 'column',
                display: 'flex'
              }}>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: 40,
                  fontWeight: 800,
                  textShadow: '1px 3px 5px black'
                }}>{tour.city}</Typography>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: 60,
                  fontWeight: 800,
                  textShadow: '1px 3px 5px black',
                  lineHeight: 1
                }}>{tour.venue}</Typography>
            </Box>
          </Box>
          
          {/* Only show RSVP button for upcoming events */}
          {!isPastEvent && (
            <Box className={classes.heroRsvpButtonContainer} sx={{
              padding: '25px'
            }}>
              <RSVPButton
                eventId={tour.id}
                eventName={`${tour.description} at ${tour.venue}`}
                datetime={tour.date} color="primary"
                location={`${tour.venue}, ${tour.address}`}
                onRSVPSuccess={handleRSVPSuccess}
              />
            </Box>
          )}
        </Container>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 4 }}>
        <Box id="aboutTour" className={classes.normalBox}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>

              {/* Left col — all text content */}
              <Grid size={{ xs: 12, md: tour.images && tour.images.length > 0 && isPastEvent ? 7 : 12 }}>

                {/* Event name as page title */}
                <Typography
                  sx={{
                    fontSize: '28px',
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 1
                  }}>
                  {tour.description}
                </Typography>

                {/* Date + organizer line */}
                <Typography
                  sx={{
                    fontSize: '15px',
                    color: '#666',
                    mb: 0.5
                  }}>
                  {formatEventDate(tour.date, tour.timezone)}
                  {!isPastEvent && ` at ${formatEventTime(tour.date, tour.timezone)}`}
                </Typography>

                {/* Venue link */}
                <Typography
                  sx={{
                    fontSize: '15px',
                    mb: 2
                  }}>
                  <Link
                    href={tour.venueLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#cc2222', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                  >
                    {tour.venue} <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                  </Link>
                  {tour.location ? ` · ${tour.location}` : ''}
                </Typography>

                {/* Highlight callout — only for past events with highlight field */}
                {isPastEvent && tour.highlight && (
                  <Box sx={{
                    borderLeft: '4px solid #cc2222',
                    pl: 2,
                    py: 0.5,
                    mb: 2,
                    backgroundColor: '#fafafa',
                    borderRadius: '0 4px 4px 0',
                  }}>
                    <Typography
                      sx={{
                        fontSize: '17px',
                        fontWeight: 600,
                        fontStyle: 'italic',
                        color: '#222'
                      }}>
                      {tour.highlight}
                    </Typography>
                  </Box>
                )}

                {/* Recap or venueDescription */}
                <Typography
                  sx={{
                    fontSize: '17px',
                    lineHeight: 1.7,
                    color: '#333',
                    mb: 2
                  }}>
                  {isPastEvent ? (tour.recap || tour.venueDescription) : tour.venueDescription}
                </Typography>

                {/* Organizer credit — past events only */}
                {isPastEvent && tour.organizer && (
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#666'
                    }}>
                    Organized by{' '}
                    <Link
                      href={tour.organizerLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#cc2222', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {tour.organizer}
                    </Link>
                    {tour.organizerTitle ? ` · ${tour.organizerTitle}` : ''}
                  </Typography>
                )}

                {/* RSVP for upcoming events */}
                {!isPastEvent && (
                  <>
                    <div className={classes.rsvpButtonContainer}>
                      <RSVPButton
                        eventId={tour.id}
                        eventName={`${tour.description} at ${tour.venue}`}
                        datetime={tour.date}
                        color="secondary"
                        location={`${tour.venue}, ${tour.address}`}
                        onRSVPSuccess={handleRSVPSuccess}
                      />
                    </div>
                    <AttendeeDisplay eventId={tour.id} refreshTrigger={attendeeRefreshTrigger} />
                  </>
                )}
              </Grid>

              {/* Right col — lead photo for past events */}
              {isPastEvent && tour.images && tour.images.length > 0 && (
                <Grid size={{ xs: 12, md: 5 }}>
                  <Box
                    component="img"
                    src={tour.images[0]}
                    alt={`${tour.venue} — ${tour.city}`}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      objectFit: 'cover',
                      maxHeight: 340,
                    }}
                  />
                </Grid>
              )}

              {/* Map for upcoming events */}
              {!isPastEvent && tour.map && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%', height: 0, overflow: 'hidden' }}>
                    <iframe
                      src={tour.map}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    />
                  </Box>
                </Grid>
              )}

              {/* Full photo gallery — past events, full width below */}
              {isPastEvent && tour.images && tour.images.length > 1 && (
                <Grid size={12}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: 700,
                      mb: 2
                    }}>
                    Event Photos
                  </Typography>
                  <EventGallery images={tour.images.slice(1)} eventId={tour.id} />
                </Grid>
              )}

            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );

  // Render function for unconfirmed cities
  const renderUnconfirmedCity = () => (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        height: { xs: 'auto', sm: 324 },
        backgroundImage: tour.image ? 
          `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${tour.image})` : 
          'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(/media/booktour/us-map-tour.jpg)',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        width: '100%'
      }}>
        <Container maxWidth="lg" sx={{ pt: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'white',
              fontSize: 48,
              fontWeight: 800,
              textShadow: '1px 3px 5px black',
              mb: 2
            }}>
            {tour.city}
          </Typography>
          <Typography
            sx={{
              color: 'white',
              fontSize: 20,
              fontWeight: 400,
              textShadow: '1px 3px 5px black'
            }}>
            Potential bookstore event location
          </Typography>
        </Container>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 4 }}>
        <Box className={classes.normalBox}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography
                  sx={{
                    fontSize: '32px',
                    fontWeight: 800,
                    lineHeight: 1.1,
                    mb: 3
                  }}>
                  Interested in a {tour.city} Event?
                </Typography>
                <Typography
                  sx={{
                    fontSize: '20px',
                    mb: 3
                  }}>
                  We're exploring bookstore locations in {tour.city}. Sign up below to be notified when we confirm a date and venue!
                </Typography>
                
                {tour.potentialVenues && tour.potentialVenues.length > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: '24px',
                        fontWeight: 600,
                        mb: 2,
                        mt: 4
                      }}>
                      Potential Venues
                    </Typography>
                    {tour.potentialVenues.map((venue, index) => (
                      <Box key={index} sx={{ mb: 3, pb: 3, borderBottom: index < tour.potentialVenues.length - 1 ? '1px solid #eee' : 'none' }}>
                        <Typography
                          sx={{
                            fontSize: '22px',
                            fontWeight: 600,
                            mb: 1
                          }}>
                          <Link href={venue.website} target="_blank" rel="noopener noreferrer" sx={{ color: '#cc2222', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                            {venue.name} <OpenInNewIcon sx={{ fontSize: 18, verticalAlign: 'middle' }} />
                          </Link>
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '18px',
                            opacity: 0.8,
                            mb: 1
                          }}>
                          {venue.location}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '16px',
                            opacity: 0.9
                          }}>
                          {venue.description}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
                
                <Typography
                  sx={{
                    fontSize: '18px',
                    mt: 4,
                    fontStyle: 'italic',
                    opacity: 0.8
                  }}>
                  Check back soon or follow our updates!
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <CityNotificationForm cityName={tour.city} />
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );

  // Render function for overview page (no ID provided)
  const renderOverviewPage = () => (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        height: { xs: 'auto', sm: 400 },
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(/media/booktour/us-map-tour.jpg)',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        width: '100%'
      }}>
        <Container maxWidth="lg" sx={{ pt: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'white',
              fontSize: 48,
              fontWeight: 800,
              textShadow: '1px 3px 5px black',
              mb: 2
            }}>
            Events & Appearances
          </Typography>
          <Typography
            sx={{
              color: 'white',
              fontSize: 20,
              fontWeight: 400,
              textShadow: '1px 3px 5px black',
              maxWidth: '600px'
            }}>
            Where Unconvention has shown up — book signings, conferences, and conversations about small business strategy
          </Typography>
        </Container>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 4 }}>
        <Box className={classes.normalBox}>
          <BookTour />
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <MetaTags article={tour ? tour : mainpage} />
      <Header />

      {isOverviewPage && renderOverviewPage()}
      {isUnconfirmedCity && renderUnconfirmedCity()}
      {isConfirmedEvent && renderConfirmedEvent()}

      {/* BookTour component - only show for individual tour/city pages, not overview */}
      {!isOverviewPage && (
        <Box id="otherTour" className={classes.normalBox} sx={{ flexDirection: 'column', alignItems: 'center' }}>
          <BookTour
            exclude={tour.id}
          />
        </Box>
      )}

      {/* Underdog Principles component - appears on all tour pages */}
      <Principles />

      <Footer />
    </>
  );
}
export default TourLanding;